import type { Setting } from "model/Workgroup";
import { isSettingEnabled } from "model/settings.logic";
import {
  isReportedTimeWindowValid,
  mergeIsoDateWithTimeInput,
  normalizeTimeReportTimesOvernight,
} from "web-apps/company/utils/timeAndDate";

const COMPANY_TIME_REPORT_STAMP_BEFORE_START_THRESHOLD =
  "CompanyTimeReportStampBeforeStartThreshold";
const COMPANY_TIME_REPORT_STAMP_AFTER_START_THRESHOLD =
  "CompanyTimeReportStampAfterStartThreshold";
const COMPANY_TIME_REPORT_STAMP_BEFORE_END_THRESHOLD =
  "CompanyTimeReportStampBeforeEndThreshold";
const COMPANY_TIME_REPORT_STAMP_AFTER_END_THRESHOLD =
  "CompanyTimeReportStampAfterEndThreshold";
const COMPANY_STAMP_CLOCK_ALLOW_EDIT_TIME = "CompanyStampClockAllowEditTime";

export interface ClockStampThresholds {
  beforeStartMinutes: number;
  afterStartMinutes: number;
  beforeEndMinutes: number;
  afterEndMinutes: number;
}

const resolveCompanySettingMinutes = (
  companySettings: Setting[] | undefined,
  key: string,
): number => {
  const raw =
    companySettings?.find((setting) => setting.key === key)?.value ?? "0";
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return parsed;
};

/** Resolves clock-stamp before/after thresholds from company settings with safe defaults. */
export const resolveClockStampThresholds = (
  companySettings: Setting[] | undefined,
): ClockStampThresholds => ({
  beforeStartMinutes: resolveCompanySettingMinutes(
    companySettings,
    COMPANY_TIME_REPORT_STAMP_BEFORE_START_THRESHOLD,
  ),
  afterStartMinutes: resolveCompanySettingMinutes(
    companySettings,
    COMPANY_TIME_REPORT_STAMP_AFTER_START_THRESHOLD,
  ),
  beforeEndMinutes: resolveCompanySettingMinutes(
    companySettings,
    COMPANY_TIME_REPORT_STAMP_BEFORE_END_THRESHOLD,
  ),
  afterEndMinutes: resolveCompanySettingMinutes(
    companySettings,
    COMPANY_TIME_REPORT_STAMP_AFTER_END_THRESHOLD,
  ),
});

/** Whether the worker can edit reported and break times when stamping. */
export const resolveCanEditTime = (
  companySettings: Setting[] | undefined,
): boolean => {
  return isSettingEnabled(companySettings, COMPANY_STAMP_CLOCK_ALLOW_EDIT_TIME);
};

export interface NormalizedStampReportTimes {
  startTime: string;
  endTime: string;
  breakStartTime: string;
  breakEndTime: string;
}

export interface StampReportTimeInputs {
  reportedStartBaseIso: string;
  reportedEndBaseIso: string;
  breakStartBaseIso: string;
  breakEndBaseIso: string;
  reportedStart: string;
  reportedEnd: string;
  breakStart: string;
  breakEnd: string;
}

/**
 * Merges reported and break HH:mm inputs onto the report's base ISO dates and
 * normalizes for overnight shifts. Returns null when any field is missing or
 * unparseable, so the same overnight-corrected times back both validation and submit.
 */
export const resolveStampReportTimes = (
  inputs: StampReportTimeInputs,
): NormalizedStampReportTimes | null => {
  const startTime = mergeIsoDateWithTimeInput(
    inputs.reportedStartBaseIso,
    inputs.reportedStart,
  );
  const endTime = mergeIsoDateWithTimeInput(
    inputs.reportedEndBaseIso,
    inputs.reportedEnd,
  );
  const breakStartTime = mergeIsoDateWithTimeInput(
    inputs.breakStartBaseIso,
    inputs.breakStart,
  );
  const breakEndTime = mergeIsoDateWithTimeInput(
    inputs.breakEndBaseIso,
    inputs.breakEnd,
  );
  if (!startTime || !endTime || !breakStartTime || !breakEndTime) {
    return null;
  }
  return normalizeTimeReportTimesOvernight({
    startTime,
    endTime,
    breakStartTime,
    breakEndTime,
  });
};

/** Whether an edited stamp clock report has a valid reported + break window. */
export const canSubmitStampReport = (
  inputs: StampReportTimeInputs,
): boolean => {
  const normalized = resolveStampReportTimes(inputs);
  return normalized != null && isReportedTimeWindowValid(normalized);
};
